'use client';

import { API_ROUTES, useApiMutation } from '@shared/api-utils';
import { getSiteConfig, type ContactPreferenceChannel } from '@shared/config';
import { createDebug } from '@shared/debug';
import { Communication } from '@shared/icons/system';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { MultistepForm } from '../../../form-controls/MultistepForm/MultistepForm';
import type { ToggleOption } from '../../../form-controls/ToggleGroup/ToggleGroup';
import { toast } from '../../../Toast/Toast';
import { useStepTracking } from '../../hooks/useStepTracking';
import { HiddenTrigger, MyAccountSheet } from '../../MyAccountSheet/MyAccountSheet';
import { renderChannelIcon } from '../utils/channelIcons';
import { buildContactPreferencesPayload, resolveEffectiveVerticals, type ContactPreferencesPayload } from '../utils/contactPreferencesPayload';
import { ChannelsForm } from './ChannelsForm';

type ChannelsEditSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactPreferences?: { verticals: string[]; channels: ContactPreferenceChannel[] };
  channelSelection?: ContactPreferenceChannel[];
  onChannelSelectionChange?: (selected: ContactPreferenceChannel[]) => void;
};

function ChannelsEditSheet({ open, onOpenChange, contactPreferences = { verticals: [], channels: [] }, channelSelection, onChannelSelectionChange }: ChannelsEditSheetProps) {
  const t = useTranslations('my_account');
  const queryClient = useQueryClient();
  const debug = createDebug({ isEnabled: false });

  const { availableChannels, channelIcons, channelTrackingNames } = getSiteConfig().CONTACT_PREFERENCES;

  const channelLabels: Record<Exclude<ContactPreferenceChannel, 'trustedPartners'>, string> = {
    email: t('settings.contact_preferences.channels_labels.email'),
    sms: t('settings.contact_preferences.channels_labels.sms'),
    phone: t('settings.contact_preferences.channels_labels.phone_call'),
    post: t('settings.contact_preferences.channels_labels.post'),
  };

  const options: ToggleOption[] = availableChannels
    .filter(channel => channel !== 'trustedPartners')
    .map(channel => ({
      value: channel,
      label: channelLabels[channel],
      icon: renderChannelIcon(channelIcons[channel] ?? 'Email'),
      trackingProps: { 'data-track-name': channelTrackingNames[channel] ?? channel },
    }));

  const { mutateAsync: saveChannels } = useApiMutation<ContactPreferencesPayload, ContactPreferencesPayload>(API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.PUT, 'PUT', {
    requestOptions: {
      credentials: true,
    },
    mutationOptions: {
      onError: (error: Error) => {
        debug.forceError('Error updating channels:', error);
      },
      onSettled: () => {
        const queryKey = ['api', API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.GET];
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  const handleSave = async (selected: ContactPreferenceChannel[]) => {
    try {
      const effectiveVerticals = resolveEffectiveVerticals(selected, contactPreferences.verticals);
      await saveChannels(buildContactPreferencesPayload(effectiveVerticals, selected));
      onOpenChange(false);
    } catch (error) {
      debug.forceError('Error updating channels:', error);
      toast(t('settings.contact_preferences.save_error'), { type: 'error' });
    }
  };

  const steps = [
    {
      id: 'channels',
      component: (
        <div className="w-full flex flex-col flex-1 gap-8 p-6 md:p-8 md:max-w-2xl overflow-y-auto">
          <div className="flex flex-col gap-5">
            {/* Step indicator */}
            <Communication size="extra-extra-large" data-testid="communication-icon" />

            {/* Content */}
            <div className="flex flex-col gap-5 items-start justify-center">
              <div className="flex flex-col gap-2 w-full">
                <h4>{t('settings.contact_preferences.channels_title')}</h4>
              </div>
            </div>
          </div>

          <ChannelsForm
            initialSelected={contactPreferences.channels}
            selected={channelSelection}
            onSelectedChange={onChannelSelectionChange}
            options={options}
            onSubmit={handleSave}
          />
        </div>
      ),
      schema: {} as unknown as import('zod').ZodType,
      'data-track-container': 'Communication Options',
    },
  ];

  // Use step tracking hook
  const { currentTrackContainer } = useStepTracking(steps);

  if (!open) return null;

  return (
    <MyAccountSheet
      trigger={<HiddenTrigger label={t('settings.contact_preferences.title')} />}
      title={t('settings.contact_preferences.title')}
      open={open}
      onOpenChange={onOpenChange}
      data-track-container={currentTrackContainer}
      data-track-feature="My Account"
    >
      <MultistepForm steps={steps} showProgressBar={false} showNavigation={false} className="flex flex-col h-full" />
    </MyAccountSheet>
  );
}

export { ChannelsEditSheet, type ChannelsEditSheetProps };
